// PR-MAY14-DELETE-BUTTONS-FE — reusable per-row delete trigger.
//
// Wraps DeleteConfirmDrawer + useCanDelete so list pages stay terse:
//   <RowDeleteButton
//     endpoint={`/api/v1/clients/${row.id}`}
//     entityKind="client"
//     label={row.code ?? row.nameEn ?? row.id}
//     onDeleted={() => refetch()}
//   />
//
// The drawer handles the reason / "type DELETE" / counts UX. This
// component just gates visibility on canDelete(role) (super_admin ||
// admin) and forwards the apiClient.delete with {reason, force} the
// drawer collects.
//
// The DELETE-BUTTONS-V2 brief: "the ACTIONS column has a header but
// renders empty" — list pages were missing the actual buttons. This
// is the missing piece.
import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api';
import { useCanDelete } from '@/lib/permissions';
import { DeleteConfirmDrawer, type DepCounts } from './DeleteConfirmDrawer';

export interface RowDeleteButtonProps {
  /** REST path the drawer will DELETE against, e.g. "/api/v1/clients/abc-uuid". */
  endpoint: string;
  /** Word to render in the drawer copy: "Delete trip?", "Delete client?", etc. */
  entityKind: string;
  /** Display label for the row being deleted ("CLI-0023", "Tamimi Group"). */
  label: string;
  /** Called after the drawer dismisses on a successful delete (refetch the list). */
  onDeleted?: () => void;
  /** Optional extra warning shown above the force checkbox. */
  forceWarning?: string;
  /** Show the icon-only button — defaults to true. Compact tables can pass false. */
  iconOnly?: boolean;
  /** className override for the trigger button. */
  className?: string;
}

export function RowDeleteButton({
  endpoint,
  entityKind,
  label,
  onDeleted,
  forceWarning,
  iconOnly = true,
  className,
}: RowDeleteButtonProps) {
  const canDelete = useCanDelete();
  const [open, setOpen] = useState(false);
  // 409 responses from the server populate this; the drawer renders the
  // dependency block and exposes the super_admin-only force checkbox.
  const [initialCounts, setInitialCounts] = useState<DepCounts | null>(null);

  if (!canDelete) return null;

  async function performDelete({ force, reason }: { force: boolean; reason: string }): Promise<{ counts?: DepCounts } | void> {
    try {
      const url = endpoint + (force ? (endpoint.includes('?') ? '&force=true' : '?force=true') : '');
      await apiClient.delete(url, { data: { reason } });
      setInitialCounts(null);
    } catch (err) {
      const data = (err as { response?: { data?: { counts?: DepCounts; reason?: string } } }).response?.data;
      if (data?.counts) {
        setInitialCounts(data.counts);
        // Re-throw so the drawer surfaces the message and stays open
        // for the operator to opt into force.
      }
      throw err;
    }
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={(e) => {
          e.stopPropagation();   // don't open the row's detail drawer
          setOpen(true);
        }}
        title={`Delete ${entityKind}`}
        data-rowdelete-button
        className={className ?? 'h-8 w-8 p-0 text-rose-700 dark:text-rose-400 hover:bg-rose-500/10'}
      >
        <Trash2 className="h-4 w-4" />
        {!iconOnly && <span className="ml-1">Delete</span>}
      </Button>
      <DeleteConfirmDrawer
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) setInitialCounts(null); }}
        entityKind={entityKind}
        label={label}
        initialCounts={initialCounts ?? undefined}
        forceWarning={forceWarning}
        performDelete={performDelete}
        onDeleted={onDeleted}
      />
    </>
  );
}
